import * as React from 'react';
import { useRef } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { AlertDialogContentProps, IHaiDialogButtonProps, IHaiDialogProps } from './IDialog';
import { useDialogMaxHeight } from './useDialogMaxHeight';
import {
  Body2,
  Button,
  Header,
  Heading4Med,
  Icon,
  ModalBody,
  ScreenInfoContext,
  ThemeNames,
  colorValue,
} from '@hai/ui-react';
import classNames from 'classnames';
import { noop } from 'lodash';
import { Modal, ModalTitle } from '../Modal/Modal';
import ModalHeader from '../Modal/SubComponents/ModalHeader';
import ModalFooter from '../Modal/SubComponents/ModalFooter';
import { ThemedComponent } from '@hai/ui-react/dist/ThemeContext';
import { useIsVisible } from 'src/js/hook/use-is-visible';
import './Dialog-argon.less';

const toArray = (children: any, flat = false) => {
  const result = Array.isArray(children) ? children : [children];
  return flat ? result.flat() : result;
};

export const isObject = (val: any) => {
  return typeof val === 'object';
};

export const deepMixin = (source: any, target: any, noArrayConcat?: any) => {
  // summary: Does a deep merge of the given target into the given source.
  // Note that arrays are concatenated during the merge unless noArrayConcat param is set to true.

  // Note: Merging joins objects, but if an array is found it concatenates the values.  As a result, merging two query configs only merges the attributes of the config, but the child arrays are concatenated not merged.
  if (source === undefined || target === undefined || source === target) {
    return;
  }

  if (!noArrayConcat && Array.isArray(source)) {
    for (const i in target) {
      source.push(target[i]); // join the array
    }
  } else if (isObject(source)) {
    for (const key in target) {
      if (source[key] && (Array.isArray(source[key]) || isObject(source[key]))) {
        deepMixin(source[key], target[key], noArrayConcat);
      } else {
        source[key] = target[key];
      }
    }
  }
  return source;
};

const renderAction = (props: any, btnConfig: IHaiDialogButtonProps, idx: number, setShow: any) => {
  const { onCancel = noop, onClose = noop } = props;
  const {
    variant = 'secondary',
    disabled = false,
    label = '',
    onClick = noop,
    type,
    close = true,
    state = 'idle',
    className = '',
    useFsMask,
    ...restBtnProp
  } = btnConfig;

  const isPromise = (func: any) => {
    return func && typeof func?.then === 'function';
  };

  const handlers = {
    onClick: (e: any) => {
      const evt = deepMixin({}, e, {
        btnConfig,
      });

      const go = onClick(evt);

      if (go === false || isPromise(go)) {
        return;
      }
      if (type === 'cancel') {
        onCancel(evt);
      }
      if (close) {
        if (setShow) {
          setShow(false);
        }
        onClose(evt); // <- close by default.
      }
    },
  };

  return (
    <Button
      key={`hai-dialog-button-${idx}`}
      className={classNames(className, useFsMask && 'fs-mask')}
      disabled={disabled}
      state={state}
      variant={variant}
      onClick={handlers.onClick}
      {...restBtnProp}
    >
      {label}
    </Button>
  );
};

/**
 *
 * @param value a number or string representing a size (width or height)
 * @returns a valid string value to assign as width or height
 */
export const sizeStringValue = (value: number | string): string => {
  return typeof value === 'number' ? `${value}px` : value;
};

export const getDimensionValue = (value: string | number) => {
  if (typeof value === 'string') {
    return value;
  }
  return `${value}px`;
};

const renderActions = (props: any, setShow: any, alignment = 'right') => {
  const { buttons } = props;

  const btnWithAlignment = buttons?.filter(
    (btn: any) => (!btn.alignment && alignment === 'right') || btn.alignment === alignment,
  );

  return btnWithAlignment?.length ? (
    <>
      {btnWithAlignment.map((btnConfig: any, idx: number) => {
        return renderAction(props, btnConfig, idx, setShow);
      })}
    </>
  ) : null;
};

export const DialogAlertContent = (props: AlertDialogContentProps) => {
  const { children, bodyText, headline } = props;

  return (
    <>
      {headline && <Heading4Med data-auto="headline-text">{headline}</Heading4Med>}
      {typeof bodyText === 'string' ? (
        <Body2 key={uuid()} color={colorValue('haiui-gray-08')} data-auto="alert-body-text">
          {bodyText}
        </Body2>
      ) : (
        <div className="hai-alert-dialog-body-text">
          {toArray(bodyText).map((text: React.ReactNode) => (
            <Body2 key={uuid()} color={colorValue('haiui-gray-08')}>
              {text}
            </Body2>
          ))}
        </div>
      )}
      {children}
    </>
  );
};

export const BaseDialog = (props: IHaiDialogProps) => {
  const {
    accentColor = colorValue('haiui-gray-07'),
    autoScroll = true,
    footer,
    title = '',
    bodyText,
    buttons = [],
    content = '',
    dataAuto,
    dialogClassName = '',
    dialogType = 'alert',
    headerCloseButton = props.dialogType === 'activity',
    headerIcon = false,
    headline,
    style,
    dynamicResizing,
    size,
    maxBodyHeight,
    onClose,
    onCancel,
    onHide,
    className,
    children,
    backdrop,
    show,
    bindComponent,
    centered = true,
    icon,
    theme = 'argon',
    useFsMask = false,
    ...rest
  } = props;

  const dlgProps = {
    style,
    size,
    onClose,
    onCancel,
    children,
    backdrop,
    centered,
    show,
    ...rest,
  };
  const screenInfoContext = React.useContext(ScreenInfoContext);
  const dataAutoVal = dataAuto ? { 'data-auto': dataAuto } : {};
  const [showContent, setShow] = React.useState(false);

  const containerRef = useRef(null);
  const indicatorHeaderRef = useRef(null);
  const indicatorFooterRef = useRef(null);
  const isTopInView = useIsVisible(indicatorHeaderRef, [show]);
  const isBottomInView = useIsVisible(indicatorFooterRef, [show]);
  const maxHeight = useDialogMaxHeight(containerRef, show);

  const renderHeader = () => {
    const hIcon =
      theme === ThemeNames.ARGON && headerIcon ? <Icon iconname={headerIcon} style={{ fill: accentColor }} /> : null;

    return (
      dialogType !== 'alert' && (
        <ModalHeader closeButton={!!onClose && headerCloseButton} className={classNames(useFsMask && 'fs-mask')}>
          {theme === ThemeNames.ARGON ? (
            <Header icon={hIcon} title={title} type="icon-header" />
          ) : (
            <ModalTitle>{title}</ModalTitle>
          )}
        </ModalHeader>
      )
    );
  };

  const renderBody = () => {
    const dialogIcon = !(theme === ThemeNames.ARGON && icon !== 'none' && dialogType === 'alert') ? null : icon ? (
      typeof icon === 'string' ? (
        <Icon iconname={icon} size="lg2" />
      ) : (
        icon
      )
    ) : (
      <Icon iconname="StatusWarning" size="lg2" />
    );

    const dialogContent = Array.isArray(content)
      ? content.map((msg, idx) => <div key={`${msg}-${idx}`}>{msg}</div>)
      : content;

    const alertContent = (
      <div className={classNames('dialog-alert-content-container', useFsMask && 'fs-mask')}>
        {bodyText ? (
          <DialogAlertContent bodyText={bodyText} headline={headline} />
        ) : (
          <>
            {headline && <Heading4Med>{headline}</Heading4Med>}
            {dialogContent}
          </>
        )}
      </div>
    );
    /*
    const maxHeightStyle = React.useCallback(() => {
      const maxH = autoScroll ? maxHeight : maxBodyHeight;
      return maxH != null ? { style: { maxHeight: sizeStringValue(maxH) } } : undefined;
    }, [autoScroll, maxHeight, maxBodyHeight]);*/
    //{...maxHeightStyle()}
    return (
      <ModalBody {...{ theme: 'argon' }}>
        <div
          ref={containerRef}
          className={classNames('modal-dialog-body-content', useFsMask && 'fs-mask')}
          data-auto="modal-dialog-body-content"
        >
          {renderIndicator('header')}
          {dialogIcon && (
            <div className="dialog-icon-container" data-auto="dialog-icon-container">
              {dialogIcon}
            </div>
          )}
          {dialogType === 'alert' ? (
            alertContent
          ) : (
            <div className="dialog-message-container" data-auto="dialog-message-container">
              {dialogContent}
            </div>
          )}
          {renderIndicator('footer')}
        </div>
      </ModalBody>
    );
  };

  const renderDivider = (type: 'header' | 'footer') => {
    const showDivider = (type === 'header' && !isTopInView) || (type === 'footer' && !isBottomInView);
    return <div className={classNames('dialog-scroll-divider', showDivider && 'show', type)} />;
  };

  const renderIndicator = (type: 'header' | 'footer') => (
    <div ref={type === 'header' ? indicatorHeaderRef : indicatorFooterRef} className="dialog-scroll-indicator" />
  );

  const renderFooter = () => {
    const showFunc = bindComponent ? setShow : null;
    const leftBtns = renderActions(props, showFunc, 'left');
    const centerBtns = renderActions(props, showFunc, 'center');
    const rightBtns = renderActions(props, showFunc);

    return dialogType === 'activity' ? (
      footer ? (
        footer
      ) : (
        <>
          {(leftBtns || centerBtns || rightBtns) && (
            <ModalFooter>
              {leftBtns ? <div className="left">{leftBtns}</div> : <div />}
              {centerBtns ? <div className="center">{centerBtns}</div> : <div />}
              {rightBtns ? <div className="right">{rightBtns}</div> : <div />}
            </ModalFooter>
          )}
        </>
      )
    ) : props.buttons?.length > 0 ? (
      <ModalFooter>{renderActions(props, showFunc)}</ModalFooter>
    ) : footer ? (
      footer
    ) : null;
  };

  const onHideHandler = () => {
    if (bindComponent) {
      setShow(false);
    } else {
      onClose(null);
    }
    onHide && onHide();
  };

  const dialogClass = `HaiDialogModal ${dialogType}  ${className} ${
    screenInfoContext ? `screen-${screenInfoContext.screenType}` : ''
  }`;

  const renderModal = () => {
    return (
      <CustomModal
        {...dlgProps}
        {...dataAutoVal}
        className={dialogClass}
        dialogClassName={`HaiDialog ${dialogType} ${dialogClassName} ${className}`}
        dynamicResizing={dynamicResizing}
        show={showContent}
        onHide={onHideHandler}
      >
        {renderHeader()}
        {renderDivider('header')}
        {renderBody()}
        {renderDivider('footer')}
        {renderFooter()}
      </CustomModal>
    );
  };

  return (
    <div className={className}>
      {bindComponent ? (
        <>
          <div style={{ width: 'fit-content', height: 'fit-content' }} onClick={() => setShow(true)}>
            {bindComponent}
          </div>
          {renderModal()}
        </>
      ) : (
        <CustomModal
          {...dlgProps}
          {...dataAutoVal}
          className={dialogClass}
          dialogClassName={`HaiDialog ${dialogType} ${dialogClassName} ${className}`}
          dynamicResizing={dynamicResizing}
          onHide={onHideHandler}
        >
          {renderHeader()}
          {renderDivider('header')}
          {renderBody()}
          {renderDivider('footer')}
          {renderFooter()}
        </CustomModal>
      )}
    </div>
  );
};

const componentPath = 'components/Dialog/Dialog';
const Dialog = (props: IHaiDialogProps) => ThemedComponent(BaseDialog, props, componentPath);

/**
 * An empty component just for the docz to display IHaiDialogButtonProps
 */
export const DialogButton = (_props: IHaiDialogButtonProps) => <></>;

const CustomModal = styled(Modal)`
  &&&& {
    .HaiModalDialog.HaiDialog {
      ${({ width }) => width && `max-width: ${width}`};
    }

    .modal-content {
      ${({ width }) => width && `width: ${width}`};
    }

    .HaiModal {
      ${({ height }) => height && `height: ${height}`}
    }
  }
`;

export default Dialog;
